import { supabase } from './supabaseClient';

/**
 * Debug helper to monitor Supabase connection and query issues
 */
export function initSupabaseDebug(): void {
  let loggedOnce = false;
  // Store original fetch to monitor requests
  const originalFetch = window.fetch;
  window.fetch = async (
    ...args: Parameters<typeof fetch>
  ): Promise<Response> => {
    const [url, config] = args as [RequestInfo | URL, RequestInit?];

    // Only intercept Supabase requests
    if (url.toString().includes(import.meta.env.VITE_SUPABASE_URL)) {
      try {
        console.group('🔍 Supabase Request');
        console.log('URL:', url);
        console.log('Method:', config?.method || 'GET');
        console.time('Response Time');

        const response = await originalFetch(...args);
        console.timeEnd('Response Time');

        // Clone response to read body while preserving original
        const clone = response.clone();
        const body = await clone.text();

        try {
          const json = JSON.parse(body);
          console.log('Response:', json);
          if (json.error) {
            console.error('Supabase Error:', json.error);
          }
        } catch {
          console.log('Response (raw):', body);
        }

        console.groupEnd();
        return response;
      } catch (error: unknown) {
        const err = error as { message?: string };
        if (!loggedOnce) {
          loggedOnce = true;
          console.error('🔴 Supabase Connection Error:', {
            message: err.message,
            url: import.meta.env.VITE_SUPABASE_URL,
          });
        } else {
          console.warn('Supabase error (silenced after first):', err?.message || 'unknown');
        }
        throw err;
      }
    }
    return originalFetch(...args);
  };

  // Test connection on init
  testConnection();
}

/**
 * Test Supabase connection and log results (delayed to avoid blocking startup)
 */
async function testConnection() {
  // Add a delay to avoid blocking app startup
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  try {
    console.group('🔄 Testing Supabase Connection');
    console.log('URL:', import.meta.env.VITE_SUPABASE_URL);
    console.time('Connection Test');

    // Use a simpler query with timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

    const { data, error } = await supabase
      .from('space_events')
      .select('count')
      .limit(1)
      .abortSignal(controller.signal);

    clearTimeout(timeoutId);
    console.timeEnd('Connection Test');

    if (error) throw error;
    console.log('✅ Connection successful');
    console.log('Database response:', data);
  } catch (error: unknown) {
    const err = error as {
      message?: string;
      code?: string;
      details?: string;
      hint?: string;
      name?: string;
    };
    
    // Don't spam console with connection errors, just log once
    if (err.name === 'AbortError') {
      console.warn('⚠️ Supabase connection timeout (this is normal during development)');
    } else {
      console.warn('⚠️ Supabase connection check failed:', {
        message: err.message,
        code: err.code,
      });
    }
  } finally {
    console.groupEnd();
  }
}
